import { ReactNode } from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';

import {
  styled,
  StyledProps,
  COLORS,
  ColorStyles,
  ColorType,
  SizeStyles,
  SizeType,
} from '../../../stitches.config';
import { Icon } from '../..';

function getRootVariants() {
  const variants = {
    size: {
      small: {
        width: '$6',
        height: '$6',
        borderRadius: '$small',
        borderWidth: '1px',
      },
      medium: {
        width: '$7',
        height: '$7',
        borderRadius: '$small',
        borderWidth: '1px',
      },
      large: {
        width: '$8',
        height: '$8',
        borderRadius: '$medium',
        borderWidth: '2px',
      },
    } as SizeStyles,
    isHighlighted: {
      true: {},
    },
    color1: COLORS.reduce((acc, color1) => {
      acc[color1] = {
        '&:focus-visible': {
          outline: `2px solid $${color1}7`,
        },
        '&:disabled': {
          borderColor: `$${color1}6`,
        },
        // '&[data-state="open"]': {
        //   borderColor: `$${color1}11`,
        // },
      };

      return acc;
    }, {} as ColorStyles),
    color2: {} as ColorStyles,
  };

  const compoundVariants = [
    // Variants by color
    ...COLORS.flatMap((color1) => [
      {
        isHighlighted: false,
        color1,
        css: {
          backgroundColor: 'white',
          borderColor: `$${color1}9`,
          '&:hover': {
            borderColor: `$${color1}10`,
          },
          '&:active': {
            backgroundColor: `$${color1}4`,
            borderColor: `$${color1}11`,
          },
        },
      },
      {
        isHighlighted: true,
        color1,
        css: {
          backgroundColor: 'white',
          borderColor: `$${color1}9`,
          '&:hover': {
            borderColor: `$${color1}10`,
          },
          '&:active': {
            backgroundColor: `$${color1}5`,
            borderColor: `$${color1}11`,
          },
        },
      },
      // Variants by a combination of two colors
      ...COLORS.filter((color2) => color1 !== color2).map((color2) => ({
        isHighlighted: true,
        color1,
        color2,
        css: {
          backgroundColor: `$${color2}3`,
          borderColor: `$${color2}9`,
          '&:hover': {
            borderColor: `$${color2}10`,
          },
          '&:active': {
            backgroundColor: `$${color2}5`,
            borderColor: `$${color2}11`,
          },
        },
      })),
    ]),
  ];

  return {
    variants,
    compoundVariants,
  };
}

function getIconVariants() {
  const variants = {
    color2: COLORS.reduce((acc, color) => {
      acc[color] = {
        color: `$${color}9`,
        '&:hover': {
          color: `$${color}10`,
        },
      };

      return acc;
    }, {} as ColorStyles),
  };

  return {
    variants,
  };
}

const StyledContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '$3',
  variants: {
    disabled: {
      true: {
        opacity: 0.5,
        pointerEvents: 'none',
      },
    },
  },
});

const StyledRoot = styled(CheckboxPrimitive.Root, {
  borderStyle: 'solid',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  ...getRootVariants(),
});

const StyledIndicator = styled(CheckboxPrimitive.Indicator, {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const StyledIcon = styled(Icon, {
  fontWeight: 600,
  ...getIconVariants(),
});

export type CheckboxProps = Omit<
  StyledProps<CheckboxPrimitive.CheckboxProps>,
  'checked'
> & {
  size?: SizeType;
  theme?: ColorType | [ColorType, ColorType];
  label?: ReactNode;
  isChecked: CheckboxPrimitive.CheckedState;
  onCheckedChange?: (isChecked: CheckboxPrimitive.CheckedState) => unknown;
};

export function Checkbox(props: CheckboxProps) {
  const {
    size = 'medium',
    theme = 'slate',
    label,
    isChecked,
    onCheckedChange = () => {},
    ...rootProps
  } = props;

  const iconColor = Array.isArray(theme) ? theme[1] : theme;

  return (
    <StyledContainer disabled={rootProps.disabled}>
      <StyledRoot
        {...rootProps}
        size={size}
        color1={Array.isArray(theme) ? theme[0] : theme}
        color2={Array.isArray(theme) ? theme[1] : undefined}
        isHighlighted={isChecked === 'indeterminate' || isChecked === true}
        checked={isChecked}
        onCheckedChange={onCheckedChange}
      >
        <StyledIndicator>
          {isChecked === 'indeterminate' && (
            <StyledIcon
              size={size}
              color2={iconColor}
              icon="check_indeterminate_small"
            />
          )}
          {isChecked === true && (
            <StyledIcon size={size} color2={iconColor} icon="check" />
          )}
        </StyledIndicator>
      </StyledRoot>

      <label htmlFor={rootProps.id}>{label}</label>
    </StyledContainer>
  );
}
